import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Clock, CreditCard, ShieldCheck } from 'lucide-react';
import { apiFetch } from '../api';

/**
 * RenewalBanner — surfaces the renewal state from /payments/renewal-status.
 *
 * v2 (0015 renewal pipeline). Returns null unless there's action to take:
 *   - action_required='pay_now'   → amber, "Renews in N days" + Renew button
 *   - action_required='frozen_pay' → red,    "24h grace window — renew to keep your plan"
 *   - action_required='expired'   → red,    "Subscription expired — back to free plan"
 *
 * Background logic comes from the daily API cron (renewal_scheduler.py).
 */
export default function RenewalBanner() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const data = await apiFetch('/payments/renewal-status').catch(() => null);
      setStatus(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-1 px-4 py-3 text-fg-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking your renewal status…
      </div>
    );
  }

  if (!status) return null;
  if (!status.plan_code || !status.billing_interval) return null;
  if (!status.action_required) return null;

  const action = status.action_required;
  const daysToRenewal = Math.max(0, status.days_until_renewal || 0);

  // Three states: pay_now (amber) → frozen_pay (red, 24h grace) → expired (red, back to free)
  const theme =
    action === 'pay_now'
      ? {
          ring: 'ring-amber-300',
          bar: 'bg-amber-500',
          bg: 'bg-amber-50 dark:bg-amber-950/40',
          text: 'text-amber-900 dark:text-amber-100',
          sub: 'text-amber-700 dark:text-amber-300',
          button: 'bg-amber-600 hover:bg-amber-700 text-white',
          icon: Clock,
          iconClass: 'text-amber-600',
        }
      : {
          ring: 'ring-rose-300',
          bar: 'bg-rose-500',
          bg: 'bg-rose-50 dark:bg-rose-950/40',
          text: 'text-rose-900 dark:text-rose-100',
          sub: 'text-rose-700 dark:text-rose-300',
          button: 'bg-rose-600 hover:bg-rose-700 text-white',
          icon: AlertTriangle,
          iconClass: 'text-rose-600 animate-pulse',
        };

  const Icon = theme.icon;

  let title, subtitle, buttonText;
  if (action === 'pay_now') {
    title = `Your subscription renews in ${daysToRenewal} day${daysToRenewal === 1 ? '' : 's'}.`;
    subtitle = `Renews on ${formatDate(status.next_renewal)} · ${status.billing_interval}.`;
    buttonText = 'Renew now';
  } else if (action === 'frozen_pay') {
    title = `Your subscription has ended. Renew within 24 hours to keep your plan.`;
    subtitle = `Your ${status.billing_interval} subscription expired. You have until ${formatDate(status.next_renewal)} to renew and reactivate your credits.`;
    buttonText = 'Renew now to keep credits';
  } else { // 'expired'
    title = `Your subscription expired. You're now on the free plan.`;
    subtitle = `Choose a plan below to restore your paid access and credits.`;
    buttonText = 'See plans';
    // For expired we send to the pricing section of /billing
    return (
      <div className={`relative overflow-hidden rounded-lg ring-1 ${theme.ring} ${theme.bg} pl-4 pr-3 py-3`}>
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${theme.bar}`} aria-hidden />
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Icon className={`h-5 w-5 ${theme.iconClass} shrink-0`} />
          <div className="flex-1 min-w-0">
            <div className={`font-semibold text-sm ${theme.text}`}>{title}</div>
            <div className={`text-xs mt-0.5 ${theme.sub}`}>{subtitle}</div>
          </div>
          <Link
            to="/billing#plans"
            className={`inline-flex items-center gap-2 rounded-md ${theme.button} px-4 py-2 text-sm font-medium transition-colors shrink-0`}
          >
            <CreditCard className="h-4 w-4" />
            {buttonText}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-lg ring-1 ${theme.ring} ${theme.bg} pl-4 pr-3 py-3`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${theme.bar}`} aria-hidden />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Icon className={`h-5 w-5 ${theme.iconClass} shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className={`font-semibold text-sm ${theme.text}`}>{title}</div>
          <div className={`text-xs mt-0.5 ${theme.sub}`}>
            {subtitle}
            {status.amount_due_cents > 0 && (
              <>
                {' '}Amount due: <span className="font-semibold tabular-nums">${(status.amount_due_cents / 100).toFixed(2)}</span>
              </>
            )}
          </div>
        </div>
        <Link
          to="/billing"
          className={`inline-flex items-center gap-2 rounded-md ${theme.button} px-4 py-2 text-sm font-medium transition-colors shrink-0`}
        >
          <CreditCard className="h-4 w-4" />
          {buttonText}
        </Link>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// re-export so other files can use the silent "all good" indicator
export function RenewalSafeIndicator({ status }) {
  if (!status || !status.plan_code || !status.billing_interval) return null;
  if (status.action_required) return null;
  if (status.days_until_renewal == null) return null;
  if (status.days_until_renewal > 14) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
      <ShieldCheck className="h-3 w-3 text-emerald-600" />
      Renews in {status.days_until_renewal} days
    </span>
  );
}